import React, { useEffect, useMemo, useState, KeyboardEvent } from 'react';
import { 
    Container, 
    Paper, 
    Typography, 
    Box, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Chip,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    SelectChangeEvent,
    Tooltip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon } from '@mui/icons-material';
import TitleBar from '../../components/TitleBar/TitleBar';
import api, { AttributeValueJson } from '../../services/api';

interface ReviewEntityAttributeKey {
    id: number;
    name: string;
    display_label: string;
    applicable_review_entity_type_ids: number[];
    applicable_review_entity_types: ReviewEntityType[];
    description: string;
    data_type: string;
    value_json?: AttributeValueJson;
    created_by: number;
    updated_by: number;
    created_at: string | null;
    updated_at: string | null;
}

interface ReviewEntityType {
    id: number;
    name: string;
    display_label: string;
    description: string;
}

interface NewAttributeKey {
    name: string;
    display_label: string;
    applicable_review_entity_type_ids: number[];
    description: string;
    data_type: string;
    value_json: AttributeValueJson;
}

interface EditAttributeKey {
    id: number;
    name: string;
    display_label: string;
    applicable_review_entity_type_ids: number[];
    description: string;
    data_type: string;
    value_json: AttributeValueJson;
}

const emptyValueJson = (): AttributeValueJson => ({ categories: [] });

/** Chip-style editor for categorical option values. */
const CategoryValuesEditor = ({
    categories,
    onChange,
}: {
    categories: string[];
    onChange: (categories: string[]) => void;
}) => {
    const [draft, setDraft] = useState('');

    const addCategory = (raw: string) => {
        const value = raw.trim();
        if (!value) return;
        if (categories.includes(value)) {
            setDraft('');
            return;
        }
        onChange([...categories, value]);
        setDraft('');
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            addCategory(draft.replace(/,/g, ''));
        } else if (event.key === 'Backspace' && !draft && categories.length > 0) {
            onChange(categories.slice(0, -1));
        }
    };

    return (
        <Box>
            <TextField
                label="Category Values"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={() => addCategory(draft)}
                fullWidth
                helperText="Type a value and press Enter or comma to add. Required for categorical attributes."
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {categories.map((category) => (
                    <Chip
                        key={category}
                        label={category}
                        onDelete={() => onChange(categories.filter((c) => c !== category))}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                ))}
            </Box>
        </Box>
    );
};

const EntityAttributes = () => {
    const [attributes, setAttributes] = useState<ReviewEntityAttributeKey[]>([]);
    const [entityTypes, setEntityTypes] = useState<ReviewEntityType[]>([]);
    const [dataTypes, setDataTypes] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [attributeToDelete, setAttributeToDelete] = useState<{id: number, name: string} | null>(null);
    const [newAttribute, setNewAttribute] = useState<NewAttributeKey>({
        name: '',
        display_label: '',
        applicable_review_entity_type_ids: [],
        description: '',
        data_type: 'CATEGORICAL',
        value_json: emptyValueJson(),
    });
    const [editAttribute, setEditAttribute] = useState<EditAttributeKey | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchAttributeKeys();
        fetchEntityTypes();
        fetchDataTypes();
    }, []);

    const filteredAttributes = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return attributes;
        }
        return attributes.filter((attribute) => {
            const typeLabels = (attribute.applicable_review_entity_types || [])
                .map((type) => type.display_label || type.name)
                .join(' ');
            const categories = (attribute.value_json?.categories || []).join(' ');
            const haystack = [
                attribute.name,
                attribute.display_label,
                attribute.description,
                attribute.data_type,
                typeLabels,
                categories,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [attributes, searchQuery]);

    const paginatedAttributes = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredAttributes.slice(start, start + rowsPerPage);
    }, [filteredAttributes, page, rowsPerPage]);

    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredAttributes.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [filteredAttributes.length, page, rowsPerPage]);

    const fetchAttributeKeys = async () => {
        setLoading(true);
        try {
            const response = await api.get('/review_entity_attribute_keys');
            setAttributes(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching review entity attribute keys:', err);
            setError('Failed to load review entity attribute keys');
        } finally {
            setLoading(false);
        }
    };

    const fetchEntityTypes = async () => {
        try {
            const response = await api.get('/review_entity_types');
            setEntityTypes(response.data);
        } catch (err) {
            console.error('Error fetching review entity types:', err);
        }
    };

    const fetchDataTypes = async () => {
        try {
            const response = await api.get('/review_entity_attribute_keys/data_types');
            setDataTypes(response.data);
        } catch (err) {
            console.error('Error fetching data types:', err);
        }
    };

    const buildValueJsonForSave = (dataType: string, valueJson: AttributeValueJson): AttributeValueJson => {
        if (dataType === 'CATEGORICAL') {
            return { ...valueJson, categories: valueJson.categories || [] };
        }
        // Preserve any non-category metadata for other types; drop empty categories list
        const rest = { ...(valueJson || {}) };
        delete rest.categories;
        return rest;
    };

    const handleOpenDialog = () => {
        setNewAttribute({
            name: '',
            display_label: '',
            applicable_review_entity_type_ids: [],
            description: '',
            data_type: 'CATEGORICAL',
            value_json: emptyValueJson(),
        });
        setOpenDialog(true);
    };

    const handleOpenEditDialog = (attribute: ReviewEntityAttributeKey) => {
        setEditAttribute({
            id: attribute.id,
            name: attribute.name,
            display_label: attribute.display_label,
            applicable_review_entity_type_ids: attribute.applicable_review_entity_type_ids,
            description: attribute.description,
            data_type: attribute.data_type || 'CATEGORICAL',
            value_json: {
                ...(attribute.value_json || {}),
                categories: attribute.value_json?.categories || [],
            },
        });
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditAttribute(null);
    };

    const handleEditInputChange = (field: string, value: string) => {
        if (editAttribute) {
            const updated: EditAttributeKey = {
                ...editAttribute,
                [field]: value,
            };
            if (field === 'data_type' && value === 'CATEGORICAL' && !updated.value_json.categories) {
                updated.value_json = { ...updated.value_json, categories: [] };
            }
            setEditAttribute(updated);
        }
    };

    const handleEditEntityTypesChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value;
        if (editAttribute) {
            setEditAttribute({
                ...editAttribute,
                applicable_review_entity_type_ids: typeof value === 'string' ? [Number(value)] : value as number[],
            });
        }
    };

    const handleUpdateAttribute = async () => {
        if (!editAttribute) return;
        try {
            await api.put('/review_entity_attribute_keys', {
                ...editAttribute,
                value_json: buildValueJsonForSave(editAttribute.data_type, editAttribute.value_json),
            });
            fetchAttributeKeys();
            handleCloseEditDialog();
            setError(null);
        } catch (err: any) {
            console.error('Error updating review entity attribute key:', err);
            setError(err.response?.data?.detail || 'Failed to update attribute key');
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleInputChange = (field: string, value: string) => {
        setNewAttribute((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === 'data_type' && value === 'CATEGORICAL' && !updated.value_json.categories) {
                updated.value_json = { ...updated.value_json, categories: [] };
            }
            return updated;
        });
    };

    const handleEntityTypesChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value;
        setNewAttribute({
            ...newAttribute,
            applicable_review_entity_type_ids: typeof value === 'string' ? [Number(value)] : value as number[],
        });
    };

    const handleSaveAttribute = async () => {
        try {
            const formattedName = newAttribute.name.trim().toUpperCase().replace(/\s+/g, '_');

            await api.post('/review_entity_attribute_keys', {
                ...newAttribute,
                name: formattedName,
                value_json: buildValueJsonForSave(newAttribute.data_type, newAttribute.value_json),
            });

            fetchAttributeKeys();
            handleCloseDialog();
        } catch (err: any) {
            console.error('Error saving review entity attribute key:', err);
            setError(err.response?.data?.detail || 'Failed to save attribute key');
        }
    };
    
    const handleDeleteClick = (id: number, name: string) => {
        setAttributeToDelete({ id, name });
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setAttributeToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (attributeToDelete) {
            try {
                await api.delete(`/review_entity_attribute_keys/${attributeToDelete.id}`);
                fetchAttributeKeys();
                setError(null);
                handleCloseDeleteDialog();
            } catch (err: any) {
                console.error('Error deleting review entity attribute key:', err);
                setError(err.response?.data?.detail || 'Failed to delete attribute key');
                handleCloseDeleteDialog();
            }
        }
    };

    const isCategoricalSaveDisabled = (dataType: string, valueJson: AttributeValueJson) =>
        dataType === 'CATEGORICAL' && !(valueJson.categories && valueJson.categories.length > 0);

    const stickyActionsSx = {
        position: 'sticky' as const,
        right: 0,
        bgcolor: 'background.paper',
        zIndex: 2,
        width: 96,
        boxShadow: '-4px 0 8px -6px rgba(0,0,0,0.25)',
    };

    const renderCompactListSummary = (items: string[], emptyLabel = '—') => {
        if (items.length === 0) {
            return (
                <Typography variant="caption" color="text.secondary">
                    {emptyLabel}
                </Typography>
            );
        }
        if (items.length <= 2) {
            return (
                <Typography variant="body2" noWrap title={items.join(', ')}>
                    {items.join(', ')}
                </Typography>
            );
        }
        return (
            <Tooltip
                title={
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {items.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </Box>
                }
                arrow
                placement="left"
            >
                <Chip
                    label={`${items.length} values`}
                    size="small"
                    variant="outlined"
                    sx={{ cursor: 'default' }}
                />
            </Tooltip>
        );
    };

    const renderEntityTypesSummary = (types: ReviewEntityType[]) => {
        const labels = types.map((type) => type.display_label || type.name).filter(Boolean);
        if (labels.length === 0) {
            return (
                <Typography variant="caption" color="text.secondary">
                    —
                </Typography>
            );
        }
        if (labels.length <= 2) {
            return (
                <Typography variant="body2" noWrap title={labels.join(', ')}>
                    {labels.join(', ')}
                </Typography>
            );
        }
        return (
            <Tooltip
                title={
                    <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 240, overflow: 'auto' }}>
                        {labels.map((label) => (
                            <li key={label}>{label}</li>
                        ))}
                    </Box>
                }
                arrow
                placement="left"
            >
                <Chip
                    label={`${labels.length} types`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ cursor: 'default' }}
                />
            </Tooltip>
        );
    };

    return (
        <>
            <TitleBar title="Review Entity Attributes" />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h4">Review Entity Attributes</Typography>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />}
                        onClick={handleOpenDialog}
                    >
                        Add Attribute
                    </Button>
                </Box>

                {error && (
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: '#FFEBEE' }}>
                        <Typography color="error">{error}</Typography>
                    </Paper>
                )}

                <Paper sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search by name, label, type, description, categories…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ minWidth: 280, flexGrow: 1, maxWidth: 480 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {filteredAttributes.length} of {attributes.length} attributes
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Display Label</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Values</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Entity Types</TableCell>
                                    <TableCell align="center" sx={{ ...stickyActionsSx, zIndex: 3, bgcolor: 'grey.50' }}>
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography>Loading...</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : attributes.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography>No entity attributes found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedAttributes.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography color="text.secondary" sx={{ py: 2 }}>
                                                No attributes match your search.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedAttributes.map((attribute) => (
                                        <TableRow key={attribute.id} hover>
                                            <TableCell sx={{ maxWidth: 160 }}>
                                                <Typography variant="body2" noWrap title={attribute.name}>
                                                    {attribute.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 160 }}>
                                                <Typography variant="body2" noWrap title={attribute.display_label}>
                                                    {attribute.display_label}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={attribute.data_type} 
                                                    size="small" 
                                                    color={
                                                        attribute.data_type === 'CATEGORICAL' ? 'primary' :
                                                        attribute.data_type === 'NUMERIC' ? 'success' :
                                                        attribute.data_type === 'PERCENTAGE' ? 'warning' : 'default'
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 140 }}>
                                                {attribute.data_type === 'CATEGORICAL' ? (
                                                    renderCompactListSummary(
                                                        attribute.value_json?.categories || [],
                                                        'No categories'
                                                    )
                                                ) : attribute.value_json && Object.keys(attribute.value_json).length > 0 ? (
                                                    <Tooltip title={JSON.stringify(attribute.value_json)} arrow>
                                                        <Chip label="Configured" size="small" variant="outlined" />
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 220 }}>
                                                <Typography variant="body2" noWrap title={attribute.description}>
                                                    {attribute.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 140 }}>
                                                {renderEntityTypesSummary(attribute.applicable_review_entity_types || [])}
                                            </TableCell>
                                            <TableCell align="center" sx={stickyActionsSx}>
                                                <IconButton 
                                                    color="primary" 
                                                    onClick={() => handleOpenEditDialog(attribute)}
                                                    size="small"
                                                    title="Edit Attribute"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton 
                                                    color="error" 
                                                    onClick={() => handleDeleteClick(attribute.id, attribute.name)}
                                                    size="small"
                                                    title="Delete Attribute"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={filteredAttributes.length}
                        page={page}
                        onPageChange={(_event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            </Container>

            {/* Add Attribute Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>Add Review Entity Attribute</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            label="Name"
                            value={newAttribute.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            fullWidth
                            required
                            helperText="Will be converted to uppercase with underscores (e.g. LOCATION)"
                        />
                        <TextField
                            label="Display Label"
                            value={newAttribute.display_label}
                            onChange={(e) => handleInputChange('display_label', e.target.value)}
                            fullWidth
                            required
                            helperText="How it will be displayed to users (e.g. Project Location)"
                        />
                        <TextField
                            label="Description"
                            value={newAttribute.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            required
                        />
                        <FormControl fullWidth required>
                            <InputLabel id="data-type-label">Data Type</InputLabel>
                            <Select
                                labelId="data-type-label"
                                value={newAttribute.data_type}
                                onChange={(e) => handleInputChange('data_type', e.target.value)}
                                input={<OutlinedInput label="Data Type" />}
                            >
                                {dataTypes.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {newAttribute.data_type === 'CATEGORICAL' && (
                            <CategoryValuesEditor
                                categories={newAttribute.value_json.categories || []}
                                onChange={(categories) =>
                                    setNewAttribute({
                                        ...newAttribute,
                                        value_json: { ...newAttribute.value_json, categories },
                                    })
                                }
                            />
                        )}
                        <FormControl fullWidth required>
                            <InputLabel id="applicable-entity-types-label">Applicable Review Entity Types</InputLabel>
                            <Select
                                labelId="applicable-entity-types-label"
                                multiple
                                value={newAttribute.applicable_review_entity_type_ids}
                                onChange={handleEntityTypesChange}
                                input={<OutlinedInput label="Applicable Review Entity Types" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((typeId) => {
                                            const type = entityTypes.find(t => t.id === typeId);
                                            return <Chip key={typeId} label={type?.display_label || typeId} />;
                                        })}
                                    </Box>
                                )}
                            >
                                {entityTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.display_label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button 
                        onClick={handleSaveAttribute} 
                        variant="contained"
                        disabled={
                            !newAttribute.name || 
                            !newAttribute.display_label || 
                            !newAttribute.description || 
                            !newAttribute.data_type ||
                            newAttribute.applicable_review_entity_type_ids.length === 0 ||
                            isCategoricalSaveDisabled(newAttribute.data_type, newAttribute.value_json)
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the attribute <strong>{attributeToDelete?.name}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Attribute Dialog */}
            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
                <DialogTitle>Edit Review Entity Attribute</DialogTitle>
                <DialogContent>
                    {editAttribute && (
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                label="Name"
                                value={editAttribute.name}
                                onChange={(e) => handleEditInputChange('name', e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Display Label"
                                value={editAttribute.display_label}
                                onChange={(e) => handleEditInputChange('display_label', e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Description"
                                value={editAttribute.description}
                                onChange={(e) => handleEditInputChange('description', e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                required
                            />
                            <FormControl fullWidth required>
                                <InputLabel id="edit-data-type-label">Data Type</InputLabel>
                                <Select
                                    labelId="edit-data-type-label"
                                    value={editAttribute.data_type}
                                    onChange={(e) => handleEditInputChange('data_type', e.target.value)}
                                    input={<OutlinedInput label="Data Type" />}
                                >
                                    {dataTypes.map((type) => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {editAttribute.data_type === 'CATEGORICAL' && (
                                <CategoryValuesEditor
                                    categories={editAttribute.value_json.categories || []}
                                    onChange={(categories) =>
                                        setEditAttribute({
                                            ...editAttribute,
                                            value_json: { ...editAttribute.value_json, categories },
                                        })
                                    }
                                />
                            )}
                            <FormControl fullWidth required>
                                <InputLabel id="edit-entity-types-label">Applicable Review Entity Types</InputLabel>
                                <Select
                                    labelId="edit-entity-types-label"
                                    multiple
                                    value={editAttribute.applicable_review_entity_type_ids}
                                    onChange={handleEditEntityTypesChange}
                                    input={<OutlinedInput label="Applicable Review Entity Types" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((typeId) => {
                                                const type = entityTypes.find(t => t.id === typeId);
                                                return <Chip key={typeId} label={type?.display_label || typeId} />;
                                            })}
                                        </Box>
                                    )}
                                >
                                    {entityTypes.map((type) => (
                                        <MenuItem key={type.id} value={type.id}>{type.display_label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog}>Cancel</Button>
                    <Button
                        onClick={handleUpdateAttribute}
                        variant="contained"
                        disabled={
                            !editAttribute ||
                            isCategoricalSaveDisabled(editAttribute.data_type, editAttribute.value_json)
                        }
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EntityAttributes;
